/** @format */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  EditorRoot,
  EditorContent,
  EditorCommand,
  EditorCommandList,
  EditorCommandItem,
  EditorCommandEmpty,
  EditorBubble,
  EditorBubbleItem,
  StarterKit,
  TiptapLink,
  TiptapUnderline,
  Placeholder,
  Command,
  renderItems,
  createSuggestionItems,
  handleCommandNavigation,
  TaskList,
  TaskItem,
  HorizontalRule,
  GlobalDragHandle,
  useEditor,
} from "novel";
import type { EditorInstance, JSONContent } from "novel";
import { cn } from "@clarix/ui";
import { normalizeRichTextEditorValue } from "@clarix/utils";
import { Image } from "@tiptap/extension-image";
import { Mention } from "@tiptap/extension-mention";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { CalloutExtension } from "./extensions/CalloutExtension";
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  TextQuote,
  Code,
  CheckSquare,
  Text as TextIcon,
  Minus,
  ImageIcon,
  TableIcon,
  MessageSquareQuote,
  Bold,
  Italic,
  Strikethrough,
  Link2,
  Undo2,
  Redo2,
} from "lucide-react";

/**
 * Parse options for every programmatic `setContent`. The default parse collapses
 * runs of spaces and drops trailing ones, which eats the space the user just
 * typed whenever a controlled parent re-feeds its value mid-edit.
 */
const SET_CONTENT_PARSE_OPTIONS = { preserveWhitespace: "full" } as const;

/**
 * `default` — the boxed comment field.
 * `canvas`  — full-height, chrome-free (the meeting-notes surface).
 * `toolbar` — `default` plus a persistent formatting bar.
 *
 * Added as a VARIANT rather than a prop on the existing ones so nothing already
 * using this component changes: a caller opts in by name, and the two original
 * variants render exactly as they did.
 */
type EditorVariant = "default" | "canvas" | "toolbar";

/**
 * Columns in the slash-command menu. Must stay in sync with the
 * `grid-template-columns` count on `.slash-command-grid`
 * (packages/ui/src/tiptap.css) — the arrow keys derive a row step from it.
 */
const SLASH_MENU_COLUMNS = 2;

/** Set while we replay a key into cmdk, so our own handler doesn't re-enter. */
let isSteppingSlashMenu = false;

/**
 * Move the slash menu's selection by `steps` of cmdk's native single step.
 *
 * cmdk owns selection state — and with it filtering, end-clamping and
 * scroll-into-view — so instead of reimplementing any of that we replay its own
 * ArrowDown/ArrowUp on the command root and let it do the work. Used to turn one
 * arrow press into a full row (↓/↑) or a single column (→/←).
 */
function stepSlashMenu(direction: "next" | "prev", steps: number) {
  const root = document.querySelector("#slash-command");
  if (!root) return;
  const key = direction === "next" ? "ArrowDown" : "ArrowUp";
  isSteppingSlashMenu = true;
  try {
    for (let i = 0; i < steps; i += 1) {
      root.dispatchEvent(
        new KeyboardEvent("keydown", { key, cancelable: true, bubbles: true }),
      );
    }
  } finally {
    isSteppingSlashMenu = false;
  }
}

interface RichTextEditorProps {
  value: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  viewOnly?: boolean;
  autoFocus?: boolean;
  variant?: EditorVariant;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  /**
   * Extra item(s) appended into the text-selection bubble menu, after the
   * built-in format controls. Kept generic (a plain node, or a function given
   * the editor) so no consumer's data layer leaks in here. The function form
   * lets a consumer act on the editor from its item — e.g. collapse the
   * selection to dismiss this bubble when it opens its own UI. Only the editing
   * variants render a bubble, so this is ignored in `viewOnly`.
   */
  bubbleMenuSlot?: ReactNode | ((editor: EditorInstance | null) => ReactNode);
}

export interface RichTextEditorHandle {
  getHTML: () => string;
  focus: () => void;
  clear: () => void;
  /**
   * Replace the pending `@query` the caller detected with a real mention node.
   *
   * `queryLength` is the length of the typed query AFTER the `@` — the trigger
   * and query are deleted together so "@ali" becomes one atomic mention rather
   * than leaving stray text behind. Inserted via commands (not a value
   * round-trip) so the cursor stays put.
   */
  insertMention: (user: { id: string; name: string }, queryLength: number) => void;
}

// Define suggestion items for slash commands
const suggestionItems = createSuggestionItems([
  {
    title: "Text",
    description: "Just start typing with plain text",
    searchTerms: ["p", "paragraph"],
    icon: <TextIcon size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode("paragraph", "paragraph")
        .run();
    },
  },
  {
    title: "To-do List",
    description: "Track tasks with a to-do list",
    searchTerms: ["todo", "task", "list", "check", "checkbox"],
    icon: <CheckSquare size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    title: "Heading 1",
    description: "Big section heading",
    searchTerms: ["title", "big", "large", "h1"],
    icon: <Heading1 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 1 })
        .run();
    },
  },
  {
    title: "Heading 2",
    description: "Medium section heading",
    searchTerms: ["subtitle", "medium", "h2"],
    icon: <Heading2 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 2 })
        .run();
    },
  },
  {
    title: "Heading 3",
    description: "Small section heading",
    searchTerms: ["subtitle", "small", "h3"],
    icon: <Heading3 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 3 })
        .run();
    },
  },
  {
    title: "Bullet List",
    description: "Create a simple bullet list",
    searchTerms: ["unordered", "point", "ul"],
    icon: <List size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered List",
    description: "Create a list with numbering",
    searchTerms: ["ordered", "ol"],
    icon: <ListOrdered size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: "Quote",
    description: "Capture a quote",
    searchTerms: ["blockquote"],
    icon: <TextQuote size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode("paragraph", "paragraph")
        .toggleBlockquote()
        .run();
    },
  },
  {
    title: "Code",
    description: "Capture a code snippet",
    searchTerms: ["codeblock"],
    icon: <Code size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    title: "Divider",
    description: "Visually divide blocks",
    searchTerms: ["horizontal rule", "hr", "line", "separator"],
    icon: <Minus size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    title: "Image",
    description: "Insert an image from URL",
    searchTerms: ["photo", "picture", "media", "img"],
    icon: <ImageIcon size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      const url = window.prompt("Enter image URL:");
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    },
  },
  {
    title: "Table",
    description: "Insert a table",
    searchTerms: ["grid", "spreadsheet", "columns", "rows"],
    icon: <TableIcon size={18} />,
    command: ({ editor, range }) => {
       
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Novel's chain type lacks insertTable; runtime API is correct
      (editor.chain().focus().deleteRange(range) as any)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
  {
    title: "Callout",
    description: "Highlight important information",
    searchTerms: ["info", "warning", "tip", "note", "highlight", "alert"],
    icon: <MessageSquareQuote size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: "callout",
          content: [{ type: "text", text: "Type your callout here…" }],
        })
        .run();
    },
  },
]);

// Configure slash command extension
const slashCommand = Command.configure({
  suggestion: {
    items: () => suggestionItems,
    render: renderItems,
  },
});

// Factory for edit-mode extensions — variant controls placeholder text + drag handle

/**
 * One toolbar button. At MODULE scope deliberately: defined inside
 * `EditorToolbar` it was a new component type on every render, so React
 * unmounted and remounted every button — and the toolbar re-renders on each
 * editor transaction, i.e. on every keystroke.
 */
function ToolbarBtn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      /*
        The two halves do DIFFERENT jobs, and running the command in `mousedown`
        conflated them.

        `mousedown` only calls `preventDefault`, which stops focus leaving
        ProseMirror — without it the selection collapses before any command can
        act on it.

        The command runs on `click`, AFTER ProseMirror has finished its own
        mousedown handling. Firing it during mousedown meant the editor processed
        the same event afterwards and reset the selection the command had just
        used, so the change was applied and then immediately undone.
      */
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (!disabled) onClick();
      }}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={cn(
        "rounded p-1.5 transition-colors",
        "hover:bg-accent focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
        active ? "bg-accent text-foreground" : "text-muted-foreground",
        disabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
      )}
    >
      {children}
    </button>
  );
}

const ToolbarSep = () => (
  <span className="bg-border mx-1 h-5 w-px shrink-0" aria-hidden="true" />
);

/**
 * The persistent formatting bar for `variant="toolbar"`.
 *
 * Driven by the editor INSTANCE rather than by Novel's bubble/slash machinery,
 * because those appear on demand and this has to be there before the writer knows
 * they want it — the point of the variant.
 *
 * `tick` exists because the instance lives in a ref: a ref change re-renders
 * nothing, so the active states (bold on, heading on) would be frozen at mount.
 * Subscribing to the editor's own transactions is what keeps them honest.
 */
function EditorToolbar({ editor: fallback }: { editor: EditorInstance | null }) {
  /*
    The editor from NOVEL'S CONTEXT, exactly as `EditorBubbleItem` takes it —
    the bubble's commands work and this bar's did not, and that was the only
    difference between them. A caller-cached instance from `onCreate` can be a
    different object from the one currently mounted.
  */
  const ctx = useEditor();
  const editor = ctx?.editor ?? fallback;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!editor) return;
    const bump = () => setTick((n: number) => n + 1);
    editor.on("transaction", bump);
    return () => {
      editor.off("transaction", bump);
    };
  }, [editor]);

  if (!editor) return null;

  const chain = () => editor.chain().focus();

  return (
    <div
      className="border-border flex flex-wrap items-center gap-0.5 border-b py-1.5"
      role="toolbar"
      aria-label="Text formatting"
    >
      {([1, 2, 3] as const).map((level) => {
        const Icon = level === 1 ? Heading1 : level === 2 ? Heading2 : Heading3;
        return (
          <ToolbarBtn
            key={level}
            title={`Heading ${level}`}
            active={editor.isActive("heading", { level })}
            onClick={() => chain().toggleHeading({ level }).run()}
          >
            <Icon className="h-4 w-4" strokeWidth={1.5} />
          </ToolbarBtn>
        );
      })}
      <ToolbarSep />
      <ToolbarBtn title="Bold" active={editor.isActive("bold")} onClick={() => chain().toggleBold().run()}>
        <Bold className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => chain().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => chain().toggleStrike().run()}
      >
        <Strikethrough className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarSep />
      <ToolbarBtn
        title="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => chain().toggleBulletList().run()}
      >
        <List className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarSep />
      <ToolbarBtn
        title="Quote"
        active={editor.isActive("blockquote")}
        onClick={() => chain().toggleBlockquote().run()}
      >
        <TextQuote className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Link"
        active={editor.isActive("link")}
        onClick={() => {
          const existing = (editor.getAttributes("link").href as string) ?? "";
          // eslint-disable-next-line no-alert -- the shared editor has no dialog
          // primitive of its own, and pulling one in would make @clarix/ui-shared
          // depend on a surface its consumers each style differently.
          const href = window.prompt("Link URL", existing);
          if (href === null) return;
          if (href === "") chain().unsetLink().run();
          else chain().setLink({ href }).run();
        }}
      >
        <Link2 className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Code block"
        active={editor.isActive("codeBlock")}
        onClick={() => chain().toggleCodeBlock().run()}
      >
        <Code className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn title="Divider" onClick={() => chain().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarSep />
      <ToolbarBtn
        title="Undo"
        disabled={!editor.can().undo()}
        onClick={() => chain().undo().run()}
      >
        <Undo2 className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
      <ToolbarBtn
        title="Redo"
        disabled={!editor.can().redo()}
        onClick={() => chain().redo().run()}
      >
        <Redo2 className="h-4 w-4" strokeWidth={1.5} />
      </ToolbarBtn>
    </div>
  );
}

function createEditExtensions(variant: EditorVariant) {
  const extensions = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      horizontalRule: false,
    }),
    TiptapUnderline,
    TiptapLink.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: "text-indigo-600 underline hover:text-indigo-700",
      },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    // @mentions. Registered as a NODE so the id survives a round-trip through
    // the editor — inserting a bare <span> would have its attributes stripped
    // by the schema. Tiptap renders `data-type="mention" data-id="<id>"`, which
    // is exactly the shape apps/crm-api's commentMentions.ts parser reads, so a
    // saved note yields server-side mentions with no extra payload.
    Mention.configure({
      HTMLAttributes: { class: "mention-inline" },
      renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
      // Tiptap's Mention ships its OWN "@" suggestion plugin. We drive the menu
      // from the consumer instead (parseMentionQuery + MentionUserMenu, the same
      // pair projects' comment composer uses), so the built-in is disabled —
      // two plugins watching the same "@" fight over the keystroke.
      suggestion: { char: "@", allow: () => false },
    }),
    HorizontalRule,
    Image.configure({
      HTMLAttributes: {
        class: "rounded-lg max-w-full h-auto my-3",
      },
    }),
    Table.configure({
      // novel runs these v3 extensions on @tiptap/core v2; a resizable table returns a
      // null node view there, so rendering any table (paste, /table, load) throws.
      resizable: false,
      HTMLAttributes: {
        class: "border-collapse table-auto w-full my-3",
      },
    }),
    TableRow,
    TableHeader,
    TableCell,
    CalloutExtension,
    slashCommand,
    Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === "heading") {
          return `Heading ${node.attrs.level}`;
        }
        return variant === "canvas"
          ? "Start writing, or type '/' for commands..."
          : "Type '/' for commands...";
      },
    }),
  ];

  if (variant === "canvas") {
    extensions.push(
      GlobalDragHandle.configure({
        dragHandleWidth: 44,
        scrollTreshold: 100,
      }) as (typeof extensions)[number],
    );
  }

  return extensions;
}

// Internal component — uses onCreate callback instead of useEditor() hook.
// Novel's useEditor() reads from TipTap's EditorContext which is INSIDE EditorContent,
// making it unavailable to this parent component. Instead, we pass initialContent
// directly to EditorContent (which maps to TipTap's content option) and capture
// the editor instance via the onCreate callback.
const RichTextEditorContent = forwardRef<
  RichTextEditorHandle,
  Omit<RichTextEditorProps, "value" | "viewOnly"> & { initialContent: string }
>(
  (
    {
      initialContent,
      onChange,
      onBlur,
      disabled,
      autoFocus,
      variant = "default",
      className,
      minHeight,
      maxHeight,
      bubbleMenuSlot,
    },
    ref,
  ) => {
    const editorRef = useRef<EditorInstance | null>(null);
    /**
     * The same instance as `editorRef`, held in state for the toolbar only.
     *
     * The ref is what every command path uses and stays the source of truth; a
     * ref assignment renders nothing, so the toolbar would never appear. Set once
     * in `onCreate` and only when the variant asks for a toolbar, so the other
     * two variants take no extra render.
     */
    const [toolbarEditor, setToolbarEditor] = useState<EditorInstance | null>(null);
    const isInitialized = useRef(false);
    const extensions = useMemo(() => createEditExtensions(variant), [variant]);

    // Imperative handle for parent components
    useImperativeHandle(ref, () => ({
      getHTML: () => editorRef.current?.getHTML() ?? "",
      focus: () => editorRef.current?.commands.focus(),
      clear: () => editorRef.current?.commands.clearContent(),
      insertMention: (user, queryLength) => {
        const editor = editorRef.current;
        if (!editor) return;
        const { from } = editor.state.selection;

        // Find the "@" by scanning BACK from the caret rather than trusting
        // `queryLength` arithmetic: the caller derives that length from the
        // decoded plain text (entities collapsed), which can drift from real
        // document positions. Bounded by queryLength + 1 so we never eat text
        // beyond the trigger, and we fall back to that arithmetic if no "@" is
        // found (e.g. an exotic selection) instead of inserting nothing.
        const maxBack = queryLength + 1;
        const searchFrom = Math.max(0, from - maxBack);
        const before = editor.state.doc.textBetween(searchFrom, from, "\n", "\n");
        const at = before.lastIndexOf("@");
        const start = at === -1 ? Math.max(0, from - maxBack) : searchFrom + at;

        editor
          .chain()
          .focus()
          .deleteRange({ from: start, to: from })
          .insertContent([
            { type: "mention", attrs: { id: user.id, label: user.name } },
            { type: "text", text: " " },
          ])
          .run();
      },
    }));

    // Track the latest initialContent so onCreate can access it even if the
    // prop changed while TipTap was still initializing (async race condition).
    const latestContentRef = useRef(initialContent);

    // The HTML this editor last handed to `onChange`. A controlled parent feeds
    // it straight back (often via a store round-trip that re-serializes it), and
    // the sync below must not treat the editor's own echo as an external change:
    // re-parsing mid-typing drops the pending trailing space, which reads as a
    // dead spacebar.
    const lastEmittedRef = useRef<string | null>(null);

    // Sync editor content when parent changes value externally
    // (e.g., switching to a different entity). Guards prevent infinite loops:
    // typing → onChange → parent state → prop change → setContent → onUpdate cycle.
    const prevContentRef = useRef(initialContent);
    useEffect(() => {
      // Keep latestContentRef fresh for onCreate to read
      latestContentRef.current = initialContent;

      const editor = editorRef.current;
      if (!editor || !isInitialized.current) return;
      if (prevContentRef.current === initialContent) return;
      prevContentRef.current = initialContent;

      const currentHtml = editor.getHTML();
      if (currentHtml === initialContent) return;
      // Comparing against the raw emission too, because `initialContent` is the
      // SANITIZED value: the sanitizer rewrites task-list markup, so the strict
      // check above never matches once the body holds a checkbox.
      if (lastEmittedRef.current === currentHtml) return;

      editor.commands.setContent(
        initialContent || "<p></p>",
        false,
        SET_CONTENT_PARSE_OPTIONS,
      );
    }, [initialContent]);

    const canvasRef = useRef<HTMLDivElement>(null);

    // Set --handle-left CSS variable on .prose-canvas so ALL block types get consistent
    // drag handle positioning. The plugin sets different inline style.left for list items
    // (-6px) vs paragraphs (22px). Instead of fighting inline styles with JS, we use
    // CSS: `.prose-canvas .drag-handle { left: var(--handle-left) !important }`.
    // CSS !important in a stylesheet ALWAYS beats inline styles without !important.
    useEffect(() => {
      if (variant !== "canvas") return;

      const updateHandleVar = () => {
        const editor = editorRef.current;
        if (!editor || !canvasRef.current) return;
        const editorRect = editor.view.dom.getBoundingClientRect();
        canvasRef.current.style.setProperty(
          "--handle-left",
          `${Math.round(editorRect.left + 2)}px`,
        );
      };

      // Initial set after editor mounts
      const timer = setTimeout(updateHandleVar, 100);
      // Update on layout changes (sidebar toggle, panel resize)
      const resizeObserver = new ResizeObserver(updateHandleVar);
      if (canvasRef.current) {
        resizeObserver.observe(canvasRef.current);
      }
      // Update on scroll (any ancestor) and window resize
      window.addEventListener("scroll", updateHandleVar, true);
      window.addEventListener("resize", updateHandleVar);
      // Also on mousemove for immediate responsiveness
      document.addEventListener("mousemove", updateHandleVar);

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
        window.removeEventListener("scroll", updateHandleVar, true);
        window.removeEventListener("resize", updateHandleVar);
        document.removeEventListener("mousemove", updateHandleVar);
      };
    }, [variant]);

    // Canvas click handler: click on empty area focuses editor
    const handleCanvasClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const editor = editorRef.current;
        if (!editor) return;

        if (target.closest(".drag-handle")) return;

        if (!target.closest(".ProseMirror")) {
          editor.commands.focus("end");
        }
      },
      [],
    );

    const editorContent = (
      <EditorContent
        className={cn(
          "focus:outline-none",
          minHeight,
          maxHeight,
          variant === "canvas"
            ? "pl-12 pr-4 py-3"
            : // The toolbar variant is already inside a bordered box drawn by its
              // wrapper, so it takes the padding WITHOUT a second frame — nesting
              // the default box here would draw a border inside a border.
              variant === "toolbar"
              ? "py-3"
              : [
                  "p-3 rounded-xl border border-indigo-100/70 bg-white",
                  "focus-within:ring-1 focus-within:ring-offset-0 focus-within:ring-ring/40 focus-within:border-ring/60",
                ],
          disabled && "opacity-50 cursor-not-allowed bg-gray-50",
          className,
        )}
         
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Novel's EditorContent expects its own extension type; our extensions are compatible at runtime
        extensions={extensions as any}
        initialContent={initialContent as unknown as JSONContent}
        editable={!disabled}
        onCreate={({ editor }) => {
          editorRef.current = editor;
          isInitialized.current = true;
          if (variant === "toolbar") setToolbarEditor(editor);

          // Race condition fix: if the parent updated the value prop WHILE
          // TipTap was still initializing, the sync useEffect already fired
          // and skipped (editor wasn't ready). Catch up now using the ref
          // which always holds the latest prop value.
          const latest = latestContentRef.current;
          if (latest && editor.isEmpty) {
            editor.commands.setContent(latest, false, SET_CONTENT_PARSE_OPTIONS);
            prevContentRef.current = latest;
          }

          if (autoFocus) {
            setTimeout(() => editor.commands.focus("end"), 50);
          }
        }}
        onUpdate={({ editor: updatedEditor }) => {
          if (isInitialized.current) {
            const html = updatedEditor.getHTML();
            lastEmittedRef.current = html;
            onChange?.(html);
          }
        }}
        onBlur={onBlur}
        editorProps={{
          handleDOMEvents: {
            keydown: (_view, event) => {
              // novel forwards only ArrowUp/ArrowDown/Enter into the slash menu.
              // The grid also navigates with Left/Right, so forward those too —
              // otherwise they fall through and move the text caret instead.
              if (
                (event.key === "ArrowRight" || event.key === "ArrowLeft") &&
                document.querySelector("#slash-command")
              ) {
                event.preventDefault();
                stepSlashMenu(event.key === "ArrowRight" ? "next" : "prev", 1);
                return true;
              }
              return handleCommandNavigation(event);
            },
          },
          // Empty task item + Enter → lift to paragraph (same behavior as bullet lists).
          // Bullet lists get this from StarterKit's ListItem extension, but TaskItem
          // doesn't include it — so we add it here.
          handleKeyDown: (_view, event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              const editor = editorRef.current;
              if (!editor || !editor.isActive("taskItem")) return false;

              const { $from } = editor.state.selection;
              if ($from.parent.textContent.length === 0) {
                return editor.chain().liftListItem("taskItem").run();
              }
            }
            return false;
          },
          attributes: {
            spellcheck: "false",
          },
        }}
      >
        {/* Slash Commands Menu — two-column grid (grid applied via the
            `slash-command-grid` CSS rule targeting cmdk's list sizer). */}
        <EditorCommand
          className="slash-command-grid z-50 h-auto max-h-[236px] w-[384px] max-w-[90vw] overflow-y-auto rounded-lg border border-border bg-surface/95 backdrop-blur-sm p-1 shadow-lg transition-all"
          onWheel={(e: React.WheelEvent) => e.stopPropagation()}
          onKeyDown={(event: React.KeyboardEvent) => {
            // novel's EditorCommand attaches its own stopPropagation handler, but
            // this prop replaces it (it spreads after), so keep that behaviour.
            event.stopPropagation();
            // Keys we replayed ourselves — let cmdk apply its native single step.
            if (isSteppingSlashMenu) return;

            // cmdk navigates in DOM order, which in a row-major grid means ↓ moves
            // *right*. Remap so the keys match the layout: ↓/↑ = one row, →/← =
            // one column. preventDefault suppresses cmdk's own single step — it
            // checks `defaultPrevented` after calling this handler.
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              stepSlashMenu(
                event.key === "ArrowDown" ? "next" : "prev",
                SLASH_MENU_COLUMNS,
              );
            } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
              event.preventDefault();
              stepSlashMenu(event.key === "ArrowRight" ? "next" : "prev", 1);
            }
          }}
        >
          <EditorCommandEmpty className="px-2 py-1 text-sm text-muted-foreground">
            No results
          </EditorCommandEmpty>
          <EditorCommandList>
            {suggestionItems.map((item) => (
              <EditorCommandItem
                key={item.title}
                value={item.title}
                onCommand={(val) => item.command?.(val)}
                className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-[3px] text-left hover:bg-accent aria-selected:bg-accent transition-colors duration-100"
              >
                <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-border bg-muted/40 [&_svg]:h-3.5 [&_svg]:w-3.5">
                  {item.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-md font-medium leading-4">{item.title}</p>
                  <p className="truncate text-xs leading-tight text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </EditorCommandItem>
            ))}
          </EditorCommandList>
        </EditorCommand>

        {/* Bubble Menu for text selection */}
        <EditorBubble className="flex w-fit max-w-[90vw] overflow-hidden rounded-lg bg-surface/95 backdrop-blur-sm shadow-overlay">
          <EditorBubbleItem
            onSelect={(editor) => editor.chain().focus().toggleBold().run()}
            className="flex cursor-pointer items-center justify-center px-3 py-2 text-xl font-medium hover:bg-accent transition-colors duration-100"
          >
            <strong>B</strong>
          </EditorBubbleItem>
          <EditorBubbleItem
            onSelect={(editor) => editor.chain().focus().toggleItalic().run()}
            className="flex cursor-pointer items-center justify-center px-3 py-2 text-xl font-medium hover:bg-accent transition-colors duration-100"
          >
            <em>I</em>
          </EditorBubbleItem>
          <EditorBubbleItem
            onSelect={(editor) =>
              editor.chain().focus().toggleUnderline().run()
            }
            className="flex cursor-pointer items-center justify-center px-3 py-2 text-xl font-medium hover:bg-accent transition-colors duration-100"
          >
            <u>U</u>
          </EditorBubbleItem>
          <EditorBubbleItem
            onSelect={(editor) => editor.chain().focus().toggleStrike().run()}
            className="flex cursor-pointer items-center justify-center px-3 py-2 text-xl font-medium hover:bg-accent transition-colors duration-100"
          >
            <s>S</s>
          </EditorBubbleItem>
          <EditorBubbleItem
            onSelect={(editor) => editor.chain().focus().toggleCode().run()}
            className="flex cursor-pointer items-center justify-center px-3 py-2 text-xl  hover:bg-accent transition-colors duration-100"
          >
            &lt;/&gt;
          </EditorBubbleItem>
          {typeof bubbleMenuSlot === "function"
            ? bubbleMenuSlot(toolbarEditor ?? editorRef.current)
            : bubbleMenuSlot}
        </EditorBubble>
      </EditorContent>
    );

    if (variant === "toolbar") {
      // No frame — the rule beneath the toolbar is the only chrome, so the
      // editor sits flush in whatever panel hosts it rather than as a card
      // inside it.
      return (
        <div>
          <EditorToolbar editor={toolbarEditor} />
          {editorContent}
        </div>
      );
    }

    if (variant === "canvas") {
      return (
        <div
          ref={canvasRef}
          className="prose-canvas flex-1 flex flex-col min-h-0"
          onClick={handleCanvasClick}
        >
          {editorContent}
        </div>
      );
    }

    return editorContent;
  },
);

RichTextEditorContent.displayName = "RichTextEditorContent";

// View-only renderer — renders HTML directly, no editor overhead
// Uses `tiptap-content` class which shares the same typography styles as ProseMirror
// Handles both HTML content and plain text (backward compat with old descriptions)
function RichTextViewer({
  value,
  variant = "default",
  className,
}: Pick<RichTextEditorProps, "value" | "variant" | "className">) {
  if (!value?.trim()) {
    return <p className="text-xl text-muted-foreground italic">No content</p>;
  }

  const html = normalizeRichTextEditorValue(value);

  return (
    <div
      className={cn(
        "tiptap-content",
        variant === "canvas" && "prose-canvas",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Main exported component
export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(({ value, viewOnly, autoFocus, variant = "default", ...props }, ref) => {
  const normalizedValue = useMemo(
    () => normalizeRichTextEditorValue(value ?? ""),
    [value],
  );

  // viewOnly: render HTML directly — no editor, no timing issues
  if (viewOnly) {
    return (
      <RichTextViewer
        value={value}
        variant={variant}
        className={props.className}
      />
    );
  }

  // Edit mode: full Novel editor (markdown + plain from API → HTML for TipTap)
  return (
    <EditorRoot>
      <RichTextEditorContent
        ref={ref}
        initialContent={normalizedValue}
        autoFocus={autoFocus}
        variant={variant}
        {...props}
      />
    </EditorRoot>
  );
});

RichTextEditor.displayName = "RichTextEditor";
